import React from "react";

import { ProblemDetailError } from "@wingspanhq/wingspan-api-sdk";

import { IS_DEV_ENV } from "../../shared/constants/environment";
import {
  getApiErrorMessage,
  getLegacyApiErrorCodes,
  isApiError,
  isLegacyApiError,
  isNetworkError
} from "./apiError";
import {
  ErrorContext,
  ErrorMapEntry,
  ErrorMessageRule,
  errorMap,
  genericErrorMessage,
  networkErrorMessage
} from "./errorMap";

/**
 * v1 error `type` → the v3 problem `code` that replaced it, so a single
 * `errorMap` entry serves call sites on either API while v1 endpoints remain.
 */
const legacyCodeAliases: Record<string, string> = {
  AlreadyExists: "ResourceConflict",
  MaxSizeExceeded: "ValidationError",
  NotFound: "ResourceNotFound"
};

const detailCodeReason = (detailCode: string | undefined): string | undefined =>
  detailCode?.split(".").pop();

const getRuleMessage = (
  rules: ErrorMessageRule[],
  originalMessage: string
): React.ReactNode => {
  for (const rule of rules) {
    if (new RegExp(rule.pattern).test(originalMessage)) {
      return typeof rule.message === "string"
        ? originalMessage.replace(new RegExp(rule.pattern), rule.message)
        : rule.message;
    }
  }
  return undefined;
};

const getCodeMessage = (
  entry: ErrorMapEntry,
  code: string | undefined,
  detailCode: string | undefined
): React.ReactNode => {
  if (code === undefined) {
    return undefined;
  }
  const codeEntry = entry.byCode?.[code];
  if (codeEntry === undefined || React.isValidElement(codeEntry)) {
    return codeEntry;
  }
  if (typeof codeEntry === "string") {
    return codeEntry;
  }
  const byDetailCode = codeEntry as { [key: string]: React.ReactNode };
  if (detailCode !== undefined && byDetailCode[detailCode] !== undefined) {
    return byDetailCode[detailCode];
  }
  const reason = detailCodeReason(detailCode);
  if (reason !== undefined && byDetailCode[reason] !== undefined) {
    return byDetailCode[reason];
  }
  return byDetailCode["Other"];
};

const getRawErrorMessage = (
  rawMessage: string | undefined,
  forceShowRawError: boolean | undefined
): React.ReactNode => {
  if (IS_DEV_ENV) {
    return `"${rawMessage}"`;
  }
  if (forceShowRawError) {
    return rawMessage;
  }
  console.error(`WS ERROR "${rawMessage}"`);
  return undefined;
};

/**
 * Resolution order, tightest match first: a `byRegex`/`byCode` rule for this
 * exact failure, then the raw API string (development only, or when the call
 * site sets `forceShowRawError`), then the context `default`, then the generic
 * apology. The raw string sits above `default` on purpose — engineers need to
 * see what the API actually said locally, and a per-context default would
 * otherwise hide it.
 */
type ResolveErrorMessageParams = {
  error: unknown;
  context: ErrorContext;
  forceShowRawError?: boolean;
};

export const resolveErrorMessage = (
  params: ResolveErrorMessageParams
): React.ReactNode => {
  if (typeof params.error === "string") {
    return params.error;
  }

  const entry: ErrorMapEntry = errorMap[params.context];
  let message: React.ReactNode;

  if (isApiError(params.error)) {
    const errorMessage = getApiErrorMessage(params.error);

    if (errorMessage !== undefined && entry.byRegex !== undefined) {
      message = getRuleMessage(entry.byRegex, errorMessage);
    }
    if (message === undefined && params.error instanceof ProblemDetailError) {
      message = getCodeMessage(
        entry,
        params.error.code,
        params.error.detailCode
      );
    }
    message =
      message ?? getRawErrorMessage(errorMessage, params.forceShowRawError);
    message = message ?? entry.default;
  } else if (isNetworkError(params.error)) {
    message = networkErrorMessage;
  } else if (isLegacyApiError(params.error)) {
    const errorMessage = getApiErrorMessage(params.error);
    const { code, detailCode } = getLegacyApiErrorCodes(params.error);

    if (errorMessage !== undefined && entry.byRegex !== undefined) {
      message = getRuleMessage(entry.byRegex, errorMessage);
    }
    message = message ?? getCodeMessage(entry, code, detailCode);
    message =
      message ??
      getCodeMessage(
        entry,
        code === undefined ? undefined : legacyCodeAliases[code],
        detailCode
      );
    message =
      message ?? getRawErrorMessage(errorMessage, params.forceShowRawError);
    message = message ?? entry.default;
  } else if (params.error instanceof Error) {
    if (entry.byRegex !== undefined) {
      message = getRuleMessage(entry.byRegex, params.error.message);
    }
    message =
      message ??
      getRawErrorMessage(params.error.message, params.forceShowRawError);
    message = message ?? entry.default;
  }

  return message ?? genericErrorMessage;
};
